import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Video, Send, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEO_EXTS = [".mp4", ".mov", ".avi", ".webm"];

function isVideo(url) {
  if (!url) return false;
  const lower = url.toLowerCase();
  return VIDEO_EXTS.some((ext) => lower.endsWith(ext));
}

function RevealMedia({ reveal }) {
  const url = `${BACKEND_URL}${reveal.url}`;
  if (reveal.type === "video" || isVideo(reveal.url)) {
    return (
      <video
        src={url}
        className="w-full h-full object-cover bg-black"
        controls
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  return (
    <img
      src={url}
      alt={reveal.caption || "Customer reveal"}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
    />
  );
}

export default function ShareReveal() {
  const [reveals, setReveals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileIsVideo, setFileIsVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  useEffect(() => {
    axios.get(`${API}/reveals`).then(({ data }) => setReveals(data)).catch(() => {});
  }, []);

  const MAX_SIZE_MB = 200;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks — reliable on cellular

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_SIZE_BYTES) {
      const sizeMB = (f.size / 1024 / 1024).toFixed(1);
      toast.error(`Your file is ${sizeMB}MB — max ${MAX_SIZE_MB}MB. For long iPhone videos, trim in your Photos app first (Edit → drag yellow handles → Save as New Clip).`);
      return;
    }
    setFile(f);
    setFileIsVideo(f.type.startsWith("video/"));
    setPreview(URL.createObjectURL(f));
  };

  // Upload a single chunk with per-chunk retry
  const uploadOneChunk = async (fd, chunkIndex, maxAttempts = 3) => {
    let attempt = 0;
    let lastErr;
    while (attempt < maxAttempts) {
      try {
        const { data } = await axios.post(`${API}/reveals/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60 * 1000,
        });
        return data;
      } catch (err) {
        lastErr = err;
        attempt += 1;
        if (attempt < maxAttempts) {
          // eslint-disable-next-line no-console
          console.warn(`Chunk ${chunkIndex} attempt ${attempt} failed, retrying...`);
          await new Promise((r) => setTimeout(r, 1500 * attempt));
        }
      }
    }
    throw lastErr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !file) {
      toast.error("Please add your name and a photo or video");
      return;
    }
    setSubmitting(true);
    setProgress(0);

    // Chunked upload: reliable on cellular; each 2MB chunk retries individually.
    const uploadId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`.padEnd(36, "0").slice(0, 36);
    const total = Math.ceil(file.size / CHUNK_SIZE);

    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("total", String(total));
        fd.append("filename", file.name);
        fd.append("name", name.trim());
        fd.append("caption", caption.trim());
        fd.append("chunk", blob, "chunk");
        await uploadOneChunk(fd, i);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success("Thank you! Your reveal will appear once approved.");
      setName("");
      setCaption("");
      setFile(null);
      setPreview(null);
      setFileIsVideo(false);
      setShowForm(false);
    } catch (err) {
      let msg = "Upload failed.";
      if (err?.code === "ECONNABORTED") {
        msg = "Upload timed out. Please connect to strong WiFi and try again.";
      } else if (err?.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail || err.response.statusText;
        msg = status === 413 ? "File chunk too large." : `Upload failed (${status}): ${detail || "please try again"}`;
      } else if (err?.request) {
        msg = "Connection unstable — try WiFi and a shorter clip.";
      } else if (err?.message) {
        msg = `Upload failed: ${err.message}`;
      }
      // eslint-disable-next-line no-console
      console.error("Reveal upload error:", err);
      toast.error(msg);
    }
    setSubmitting(false);
    setProgress(0);
  };

  return (
    <section id="share-reveal" data-testid="share-reveal-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 section-glow-gold" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-great-vibes text-[#FF007F] text-2xl md:text-3xl mb-4">Show Off Your Treasure</p>
          <h2 className="font-playfair text-[#D4AF37] text-4xl md:text-5xl font-bold">Share Your Reveal</h2>
          <div className="gold-line w-24 mx-auto mt-6 mb-6" />
          <p className="font-montserrat text-white/40 text-sm max-w-md mx-auto">
            Got your mystery scoop? Share your unboxing photo or video and join our community of queens!
          </p>
        </motion.div>

        {reveals.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12"
            data-testid="reveals-grid"
          >
            {reveals.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group relative rounded-xl overflow-hidden border border-[#D4AF37]/10 aspect-square"
                data-testid={`reveal-card-${r.id}`}
              >
                <RevealMedia reveal={r} />
                {(r.type === "video" || isVideo(r.url)) && (
                  <div className="absolute top-2 left-2 bg-black/60 rounded-full p-1.5 pointer-events-none">
                    <Video className="w-3 h-3 text-[#D4AF37]" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-3 pointer-events-none">
                  <p className="font-playfair text-white text-sm font-semibold">{r.name}</p>
                  {r.caption && <p className="font-montserrat text-white/60 text-[10px] mt-0.5 line-clamp-2">{r.caption}</p>}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="flex justify-center">
          <AnimatePresence mode="wait">
            {!showForm ? (
              <motion.button
                key="btn"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowForm(true)}
                data-testid="share-reveal-open-btn"
                className="btn-gold px-8 py-4 rounded-full text-sm font-montserrat flex items-center gap-3"
              >
                <Camera className="w-4 h-4" />
                Share Your Reveal
              </motion.button>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                onSubmit={handleSubmit}
                className="glass-card border border-[#D4AF37]/15 rounded-2xl p-6 sm:p-8 w-full max-w-md relative"
                data-testid="share-reveal-form"
              >
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setFile(null); setPreview(null); setFileIsVideo(false); }}
                  className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors"
                  data-testid="share-reveal-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>

                <h3 className="font-playfair text-white text-lg mb-5">Share Your Unboxing</h3>

                <div className="space-y-4">
                  <Input
                    data-testid="reveal-name-input"
                    className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-11 font-montserrat text-sm placeholder:text-white/30"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Input
                    data-testid="reveal-caption-input"
                    className="bg-black/50 border-[#D4AF37]/30 focus:border-[#D4AF37] text-white h-11 font-montserrat text-sm placeholder:text-white/30"
                    placeholder="Caption (optional)"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                  />

                  {!preview ? (
                    <label
                      className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-[#D4AF37]/20 rounded-xl cursor-pointer hover:border-[#D4AF37]/40 transition-colors"
                      data-testid="reveal-upload-area"
                    >
                      <div className="flex gap-3 mb-2">
                        <Camera className="w-6 h-6 text-[#D4AF37]/40" />
                        <Video className="w-6 h-6 text-[#D4AF37]/40" />
                      </div>
                      <span className="font-montserrat text-white/30 text-xs">Tap to upload photo or video</span>
                      <span className="font-montserrat text-white/20 text-[10px] mt-1">JPG, PNG, MP4, MOV (max 200MB)</span>
                      <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFile} className="hidden" />
                    </label>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden h-36" data-testid="reveal-preview">
                      {fileIsVideo ? (
                        <video src={preview} className="w-full h-full object-cover" controls muted />
                      ) : (
                        <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => { setFile(null); setPreview(null); setFileIsVideo(false); }}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="reveal-submit-btn"
                    className="btn-gold w-full py-3 rounded-full text-sm font-montserrat flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {submitting ? (progress > 0 && progress < 100 ? `Uploading ${progress}%...` : progress >= 100 ? "Processing..." : "Uploading...") : "Submit Reveal"}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
